import { useRef } from 'react';
import styled from 'styled-components';
import { useScene } from '../../../hooks';
import { useCursor, useKeyboard, useMouse } from '../../../hooks/useEditor';
import { CursorData, KeyboardData, MouseData } from '../../../types/hooks';
import { LauncherElementTypeName } from '../../../types/launcherElement';
import { commandOptions, variableOptions } from '../../../types/options';

export const ElementTreeNode = ({ node, style, dragHandle }: any) => {
  const scene = useScene();
  const [mouse] = useMouse<MouseData>();
  const [keyboard] = useKeyboard<KeyboardData>();
  const [, setCursor] = useCursor<CursorData>();

  const isActive = scene.selectedElementIDs.includes(node.data.id);
  const isHovered = mouse.hoveredElementID === node.data.id;

  // Select clicked Element
  const onMouseClick = () => {
    const clickedElementID = node.data.id;
    if (scene.selectedElementIDs.includes(clickedElementID)) return;

    if (keyboard.keysDown?.includes('ControlLeft')) {
      // Hold ctrl and click to multiselect
      scene.setSelectedElementIDs((prev) => [...prev, clickedElementID]);
    } else {
      // Just click to select single element
      scene.setSelectedElementIDs([clickedElementID]);
    }
  };

  // Show Cursor on hovered Element
  const onMouseEnter = () => {
    const launcherWindowDiv = document.getElementById('launcherWindow');
    const launcherWindowRect = launcherWindowDiv?.getBoundingClientRect()!;

    const elementDiv = document.getElementById(node.data.id);
    const elementRect: {
      x?: number;
      y?: number;
      width?: number;
      height?: number;
    } = elementDiv?.getBoundingClientRect() || {};

    const elementDim = {
      x: elementRect.x && +(elementRect.x - launcherWindowRect.x).toFixed(2),
      y: elementRect.y && +(elementRect.y - launcherWindowRect.y).toFixed(2),
      width: elementRect.width && +elementRect.width.toFixed(2),
      height: elementRect.height && +elementRect.height.toFixed(2),
    };

    setCursor({
      isVisible: true,
      x: (elementDim.x || 0) + scene.offset.x,
      y: (elementDim.y || 0) + scene.offset.y,
      width: elementDim?.width,
      height: elementDim?.height,
    });
  };

  // Hide Cursor
  const onMouseLeave = () => {
    setCursor({
      isVisible: false,
    });
  };

  const getIconClassName = (type: LauncherElementTypeName) => {
    switch (type) {
      case 'div':
        return 'icon-box';
      case 'button':
        return 'icon-interaction';
      case 'text':
        return 'icon-text';
      case 'link':
        return 'icon-hyperlink';
      case 'variableText':
        return 'icon-variable';
      case 'img':
        return 'icon-img';
      case 'appTaskProgressBar':
        return 'icon-progressbar';
      case 'iframe':
        return 'icon-iframe';
    }

    return null;
  };

  const iconClassName = getIconClassName(node.data.type);

  const getSecondaryText = (node: any) => {
    if (node.data?.props?.href) {
      return <i>{node.data.props.href}</i>;
    } else if (node.data?.props?.text) {
      return <i>{node.data.props.text}</i>;
    } else if (node.data?.props?.command?.type) {
      const foundOption = commandOptions.find(
        (option) => option.value.type === node.data.props.command.type
      );
      return `( ${foundOption?.label || ''} )`;
    } else if (node.data?.props?.variable) {
      const foundOption = variableOptions.find(
        (option) => option.value === node.data.props.variable
      );
      return `[ ${foundOption?.label || ''} ]`;
    }

    return '';
  };

  const inputRef = useRef<HTMLInputElement | null>(null);
  inputRef.current?.focus();

  return (
    <Node
      style={{ ...style }}
      ref={node.isEditing ? null : dragHandle}
      onClick={(e) => e.stopPropagation()}
    >
      <TreeNodeInnerContainer
        $node={node}
        $isActive={isActive}
        $isHovered={isHovered}
        onClick={onMouseClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDoubleClick={() => node.edit()}
      >
        {!node.isLeaf && (
          <DirButton
            onClick={(e) => {
              e.stopPropagation();
              node.toggle();
            }}
            $open={node.isOpen}
          >
            <i className="icon-right-open-mini" />
          </DirButton>
        )}
        <TreeNodeText>
          {iconClassName && <i className={iconClassName} />}
          {node.isEditing ? (
            <input
              ref={inputRef}
              type="text"
              defaultValue={node.data.name}
              onBlur={(e) => {
                node.submit(e.currentTarget.value);
                node.data.name = e.currentTarget.value;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  node.submit(e.currentTarget.value);
                  node.data.name = e.currentTarget.value;
                }
                if (e.key === 'Escape') node.reset();
              }}
            />
          ) : (
            <span>{node.data.name || getSecondaryText(node)}</span>
          )}
          <span className="secondaryText">
            {node.data.name && getSecondaryText(node)}
          </span>
        </TreeNodeText>
      </TreeNodeInnerContainer>
    </Node>
  );
};

const Node = styled.div`
  height: 100%;
`;

const DirButton = styled.div<{ $open?: boolean }>`
  position: absolute;
  left: 0px;
  z-index: 1;
  cursor: pointer;
  min-width: 20px;
  min-height: 20px;

  display: flex;
  align-items: center;
  justify-content: center;

  transition: transform 0.3s cubic-bezier(0.075, 0.82, 0.165, 1);
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
`;

const TreeNodeInnerContainer = styled.div<{
  $node: any;
  $isActive?: boolean;
  $isHovered?: boolean;
}>`
  position: relative;
  box-sizing: border-box;
  max-width: 300px;
  height: 100%;
  max-height: 100%;
  display: flex;
  align-items: center;
  padding: 0 5px;
  animation: fadeIn 0.5s ease;

  color: ${({ $isActive }) =>
    $isActive ? 'var(--accent-color)' : 'var(--text-color)'};

  border-radius: 12px;
  opacity: ${({ $node }) => ($node.isDragging ? 0.5 : 1)};

  background-color: ${({ $isActive, $isHovered }) =>
    $isActive
      ? 'var(--panel-bg-color)'
      : $isHovered
      ? 'var(--panel-bg-color-hover)'
      : 'transparent'};
  border: ${({ $node }) =>
    $node.willReceiveDrop
      ? '1px dashed var(--text-color)'
      : '1px dashed transparent'};

  transition:
    background-color 0.3s ease,
    color 0.3s ease,
    border 0.3s ease;
  &:hover {
    background-color: var(--panel-bg-color-hover);
  }
`;

const TreeNodeText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  padding: 0 8px;

  display: flex;
  align-items: center;

  font-size: 12px;
  font-weight: 300;

  & > i {
    opacity: 0.5;
    padding-right: 3px;
    font-size: 16px;
  }

  & > .secondaryText {
    padding: 0 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-left: 5px;
    color: var(--text-color-secondary);
    font-size: 10px;
    font-weight: 400;
  }

  & > input {
    border-radius: 5px;
    padding: 2px 5px;
    border: 1px solid var(--accent-color);
  }
`;
